/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { buildItemPhotoEndpoint } from '../api';
import { loadRemoteCatalogProductsByIds } from '../features/catalogCache';
import { resolveCartItemUnitPrice } from '../features/cartPricing';
import {
  buildCartDetails,
  buildCartDescription,
  fitLabelToEnum,
  genderLabelToEnum,
  getFabricLabelFromMaterial,
  normalizeColorHex,
  normalizeFabricBlendLabel,
  normalizeFitLabel,
  normalizeGenderLabel,
  normalizeMaterialRecord,
  normalizeSizeValue,
  type CartMaterial,
  type CartMeasurements,
} from '../features/cartItemConfiguration';
import { resolveLocalizedText } from '../i18n/locale';
import { enqueueCustomizationSpecs } from '../features/customizationSpecs';
import { enqueueShoppingCartMutation } from '../features/shoppingCartQueue';
import type { ShopProduct } from '../mocks/shopProducts';
import {
  AUTH_FLAG_KEY,
  AUTH_STATE_CHANGE_EVENT,
  AUTH_TOKEN_KEY,
  AUTH_USER_KEY,
  CART_STORAGE_KEY,
  getAuthenticatedUploadAccountId,
  fetchAwsApi,
  getApiErrorMessage,
  getAuthenticatedUser,
  isAdminSession,
  isAuthenticatedSession,
  isApiResponseOk,
  getSessionAccountId,
  parseJsonValue,
  parseApiResponse,
  readStorageValue,
  sanitizeStoredCartItem,
  sanitizeStoredCartItems,
  writeStorageValue,
} from '../security';

export interface CartItem {
  id: string;
  itemId?: string;
  cadFileId?: string;
  s3Key?: string;
  remoteCartItemIds?: string[];
  measurements?: CartMeasurements;
  size?: string;
  fit?: string;
  color?: string;
  fabricBlend?: string;
  material?: CartMaterial;
  gender?: string;
  type: 'hairstyle' | 'clothing' | 'nails';
  title: string;
  description: string;
  image: string;
  price: number;
  quantity: number;
  details?: {
    [key: string]: string[];
  };
}

interface CartContextType {
  items: CartItem[];
  refreshCart: () => Promise<CartItem[]>;
  addItem: (item: Omit<CartItem, 'id'>, options?: { syncRemote?: boolean }) => Promise<void>;
  removeItem: (id: string) => Promise<void>;
  updateQuantity: (id: string, quantity: number) => Promise<void>;
  updateItem: (id: string, updates: Partial<Omit<CartItem, 'id'>>) => Promise<void>;
  clearCart: (options?: { syncRemote?: boolean }) => Promise<void>;
  getTotalPrice: () => number;
  getItemCount: () => number;
}

const fallbackCartContext: CartContextType = {
  items: [],
  refreshCart: async () => [],
  addItem: async () => undefined,
  removeItem: async () => undefined,
  updateQuantity: async () => undefined,
  updateItem: async () => undefined,
  clearCart: async () => undefined,
  getTotalPrice: () => 0,
  getItemCount: () => 0,
};

const CartContext = createContext<CartContextType>(fallbackCartContext);
const REMOTE_CART_REFRESH_INTERVAL_MS = 5000;
const cartHydrationPromiseByAccount = new Map<string, Promise<CartItem[]>>();
const cartHydrationTimestampByAccount = new Map<string, number>();

function getCartStorageKey(accountId: string) {
  return `${CART_STORAGE_KEY}:${accountId}`;
}

function getActiveCartAccountId() {
  return getSessionAccountId(getAuthenticatedUser());
}

function readCartItemsForAccount(accountId: string) {
  if (!accountId) {
    return [];
  }

  const rawValue = readStorageValue(getCartStorageKey(accountId));
  return ensureUniqueCartItemIds(sanitizeStoredCartItems(parseJsonValue(rawValue)));
}

function isAuthenticatedCartAccount(accountId: string) {
  const authenticatedAccountId = getAuthenticatedUploadAccountId(getAuthenticatedUser());
  return Boolean(accountId && authenticatedAccountId && accountId === authenticatedAccountId && isAuthenticatedSession());
}

function shouldSyncRemoteCart(accountId: string) {
  return Boolean(accountId);
}

export function shouldHydrateRemoteCartItems(accountId: string, isAdmin = isAdminSession()) {
  return shouldSyncRemoteCart(accountId) && !isAdmin;
}

function isUuid(value: string | undefined) {
  if (!value) {
    return false;
  }

  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getRemoteCartItemIds(remoteCartItemIds: string[] | undefined) {
  return Array.from(new Set((remoteCartItemIds ?? []).filter((id) => isUuid(id))));
}

function toIdSlug(value: string | undefined, fallback: string) {
  if (!value) {
    return fallback;
  }

  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30);

  return slug || fallback;
}

function buildFallbackCartItemId(item: CartItem, index: number) {
  const parts = [
    toIdSlug(item.itemId, 'item'),
    toIdSlug(item.size, 'size'),
    toIdSlug(item.fit, 'fit'),
    toIdSlug(item.fabricBlend, 'fabric'),
    toIdSlug(item.color, 'color'),
    toIdSlug(item.gender, 'gender'),
    `${index + 1}`,
  ];

  return `cart-${parts.join('-')}`;
}

function ensureUniqueCartItemIds(items: CartItem[]) {
  const usedIds = new Set<string>();

  return items.map((item, index) => {
    const remoteCandidates = getRemoteCartItemIds(item.remoteCartItemIds);
    const candidateIds = [item.id, ...remoteCandidates, buildFallbackCartItemId(item, index)];
    let nextId = candidateIds.find((candidate) => candidate && !usedIds.has(candidate)) ?? '';

    if (!nextId) {
      const fallbackBase = buildFallbackCartItemId(item, index);
      let suffix = 2;
      nextId = fallbackBase;
      while (usedIds.has(nextId)) {
        nextId = `${fallbackBase}-${suffix}`;
        suffix += 1;
      }
    }

    usedIds.add(nextId);

    return nextId === item.id
      ? item
      : {
          ...item,
          id: nextId,
        };
  });
}

export function mergeHydratedCartItems(
  localOnlyItems: CartItem[], remoteItems: CartItem[], matchedLocalItemIds: Set<string>,
) {
  return [...localOnlyItems.filter((item) => !matchedLocalItemIds.has(item.id)), ...remoteItems];
}

function getCartItemConfigurationSignature(item: CartItem) {
  return JSON.stringify({
    itemId: item.itemId ?? '',
    cadFileId: item.cadFileId ?? '',
    s3Key: item.s3Key ?? '',
    type: item.type,
    title: item.title,
    size: item.size ?? '',
    fit: item.fit ?? '',
    fabricBlend: item.fabricBlend ?? '',
    color: item.color ?? '',
    gender: item.gender ?? '',
    material: item.material ?? {},
    measurements: item.measurements ?? {},
    details: item.details ?? {},
  });
}

function isSameCartSpec(
  item: CartItem,
  {
    size,
    fit,
    color,
    fabricBlend,
    gender,
  }: {
    size: string | undefined;
    fit: string | undefined;
    color: string | undefined;
    fabricBlend: string | undefined;
    gender: string | undefined;
  }
) {
  return (
    (item.size ?? '') === (size ?? '')
    && (item.fit ?? '') === (fit ?? '')
    && (item.color ?? '') === (color ?? '')
    && (item.fabricBlend ?? '') === (fabricBlend ?? '')
    && (item.gender ?? '') === (gender ?? '')
  );
}

function extractRemoteCartRows(payload: unknown) {
  if (!isRecord(payload) || !Array.isArray(payload.cart_items)) {
    return [];
  }

  return payload.cart_items.filter(isRecord);
}

function getCartItemType(product: ShopProduct | undefined, fallbackType?: CartItem['type']): CartItem['type'] {
  if (fallbackType) {
    return fallbackType;
  }

  switch (product?.category) {
    case 'Hair':
      return 'hairstyle';
    case 'Nails':
    case 'Makeup':
      return 'nails';
    default:
      return 'clothing';
  }
}

function buildShoppingCartQuery(accountId: string, extraParams?: Record<string, string>) {
  const identityParams = isAuthenticatedCartAccount(accountId)
    ? { account_id: accountId }
    : { session_id: accountId };
  const searchParams = new URLSearchParams({
    ...identityParams,
    ...(extraParams ?? {}),
  });

  return `/api/shopping_cart?${searchParams.toString()}`;
}

export function buildCustomizationSpecsPayload(accountId: string, item: CartItem) {
  const itemId = item.itemId?.trim();
  if (!itemId) {
    return null;
  }

  const size = item.size?.trim() || 'One Size';
  const fabricBlend = item.fabricBlend?.trim() || getFabricLabelFromMaterial(item.material) || 'Standard';
  const color = item.color?.trim() || null;
  const fit = fitLabelToEnum(item.fit);
  const quantity = Number.isInteger(item.quantity) && item.quantity > 0 ? item.quantity : 1;

  return {
    store: typeof __STORE_NAME__ === 'string' ? __STORE_NAME__ : 'vadauro',
    ...(isAuthenticatedCartAccount(accountId) ? {} : { session_id: accountId }),
    item_id: itemId,
    size,
    fabric_blend: fabricBlend,
    color,
    ...(fit ? { fit } : {}),
    quantity,
  };
}

export function extractRemoteCartItemId(payload: unknown) {
  const candidates = [
    payload,
    isRecord(payload) ? payload.cart_item : null,
    isRecord(payload) ? payload.cartItem : null,
    isRecord(payload) ? payload.item : null,
    isRecord(payload) ? payload.shopping_cart : null,
    isRecord(payload) && Array.isArray(payload.cart_items) ? payload.cart_items[0] : null,
  ];

  for (const candidate of candidates) {
    if (!isRecord(candidate)) {
      continue;
    }

    for (const value of [candidate.id, candidate.cart_item_id, candidate.cartItemId]) {
      const id = typeof value === 'string' ? value.trim() : '';
      if (isUuid(id)) {
        return id;
      }
    }
  }

  return '';
}

function normalizeComparableCartText(value: unknown) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function normalizeCartColorImageToken(value: unknown) {
  const normalizedHex = normalizeColorHex(value);
  if (normalizedHex) {
    return normalizedHex.toLowerCase();
  }

  return typeof value === 'string' || typeof value === 'number'
    ? String(value)
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    : '';
}

export function resolveRemoteCartItemImage(
  product: Pick<ShopProduct, 'image' | 'colorImageMap'>,
  row: unknown,
  localImage?: string,
) {
  if (isRecord(row) && product.colorImageMap) {
    const imageGroupsByToken = new Map(
      Object.entries(product.colorImageMap).map(([colorKey, images]) => [
        normalizeCartColorImageToken(colorKey),
        images,
      ])
    );
    const colorTokens = [
      row.color_code,
      row.colorCode,
      row.inventory_color,
      row.color,
      row.color_name,
      row.colorName,
    ].map(normalizeCartColorImageToken).filter(Boolean);

    for (const token of colorTokens) {
      const firstColorImage = imageGroupsByToken.get(token)?.find((image) => Boolean(image));
      if (firstColorImage) {
        return firstColorImage;
      }
    }
  }

  return localImage || product.image || '';
}

function extractPrimaryS3KeyFromRow(row: unknown) {
  if (!isRecord(row)) {
    return '';
  }

  const rawPhotoList = row.s3_keys ?? row.s3Keys;
  const photoList = Array.isArray(rawPhotoList)
    ? rawPhotoList
    : typeof rawPhotoList === 'string'
      ? parseJsonValue(rawPhotoList)
      : null;
  if (!Array.isArray(photoList) || photoList.length === 0) {
    return '';
  }

  const photos = photoList
    .filter(isRecord)
    .sort((a, b) => {
      const aSortOrder = Number(a.sort_order ?? a.sortOrder ?? 0);
      const bSortOrder = Number(b.sort_order ?? b.sortOrder ?? 0);
      return aSortOrder - bSortOrder;
    });
  if (photos.length === 0) {
    return '';
  }

  const primaryPhoto = photos.find((photo) => photo.is_primary === true || photo.isPrimary === true) ?? photos[0];
  return (
    (typeof primaryPhoto.s3_key === 'string' && primaryPhoto.s3_key.trim())
    || (typeof primaryPhoto.s3Key === 'string' && primaryPhoto.s3Key.trim())
    || (typeof primaryPhoto.objectKey === 'string' && primaryPhoto.objectKey.trim())
    || ''
  );
}

export function resolveRemoteCartPrimaryImageUrl(row: unknown) {
  const objectKey = extractPrimaryS3KeyFromRow(row);
  return objectKey ? buildItemPhotoEndpoint(objectKey) : '';
}

export function resolveRemoteCartSizeValue(row: unknown) {
  if (!isRecord(row)) {
    return undefined;
  }

  return (
    normalizeSizeValue(row.inventory_size_value)
    ?? normalizeSizeValue(row.size_value)
    ?? normalizeSizeValue(row.size)
    ?? normalizeSizeValue(row.accessories_size)
  );
}

export function findMatchingRemoteCartRowIdForCartItem(item: CartItem, rows: unknown[]) {
  const itemId = typeof item.itemId === 'string' ? item.itemId.trim() : '';
  if (!itemId) {
    return '';
  }

  const itemSize = normalizeComparableCartText(item.size);
  const itemFit = fitLabelToEnum(item.fit);
  const itemGender = genderLabelToEnum(item.gender);
  const itemColor = normalizeComparableCartText(item.color);

  for (const row of rows) {
    if (!isRecord(row)) {
      continue;
    }

    const rowId = typeof row.id === 'string' ? row.id.trim() : '';
    const rowItemId = typeof row.item_id === 'string' ? row.item_id.trim() : '';
    if (!isUuid(rowId) || rowItemId !== itemId) {
      continue;
    }

    const rowSizes = [row.size, row.size_value, row.inventory_size_value, row.accessories_size]
      .map(normalizeComparableCartText)
      .filter(Boolean);
    if (itemSize && rowSizes.length > 0 && !rowSizes.includes(itemSize)) {
      continue;
    }

    const rowFit = fitLabelToEnum(row.fit);
    if (itemFit && rowFit && itemFit !== rowFit) {
      continue;
    }

    const rowGender = genderLabelToEnum(row.gender);
    if (itemGender && rowGender && itemGender !== rowGender) {
      continue;
    }

    const rowColors = [row.color, row.inventory_color, row.color_name]
      .map(normalizeComparableCartText)
      .filter(Boolean);
    if (itemColor && rowColors.length > 0 && !rowColors.includes(itemColor)) {
      continue;
    }

    return rowId;
  }

  return '';
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [activeAccountId, setActiveAccountId] = useState(() => getActiveCartAccountId());
  const [items, setItems] = useState<CartItem[]>(() => readCartItemsForAccount(getActiveCartAccountId()));
  const lastSyncedAccountIdRef = useRef(activeAccountId);

  const persistItems = useCallback((accountId: string, nextItems: CartItem[]) => {
    const sanitizedItems = ensureUniqueCartItemIds(sanitizeStoredCartItems(nextItems));

    if (!accountId) {
      return sanitizedItems;
    }

    writeStorageValue(getCartStorageKey(accountId), JSON.stringify(sanitizedItems));
    return sanitizedItems;
  }, []);

  const deleteRemoteCartItems = useCallback(async (remoteCartItemIds: string[] | undefined) => {
    const uniqueIds = getRemoteCartItemIds(remoteCartItemIds);

    if (!shouldSyncRemoteCart(activeAccountId) || uniqueIds.length === 0) {
      return;
    }

    await Promise.all(uniqueIds.map(async (remoteCartItemId) => {
      await enqueueShoppingCartMutation({
        accountId: activeAccountId,
        authenticated: isAuthenticatedCartAccount(activeAccountId),
        cartItemId: remoteCartItemId,
        method: 'DELETE',
      });
    }));
  }, [activeAccountId]);

  const updateRemoteCartItemQuantity = useCallback(async (remoteCartItemId: string, quantity: number) => {
    if (!shouldSyncRemoteCart(activeAccountId) || !isUuid(remoteCartItemId)) {
      return;
    }

    await enqueueShoppingCartMutation({
      accountId: activeAccountId,
      authenticated: isAuthenticatedCartAccount(activeAccountId),
      cartItemId: remoteCartItemId,
      method: 'PATCH',
      quantity,
    });
  }, [activeAccountId]);

  const createRemoteCartItem = useCallback(async (item: CartItem) => {
    if (!shouldSyncRemoteCart(activeAccountId)) {
      return '';
    }

    const customizationPayload = buildCustomizationSpecsPayload(activeAccountId, item);
    if (customizationPayload) {
      await enqueueCustomizationSpecs(customizationPayload);
      return '';
    }

    return '';
  }, [activeAccountId]);

  const hydrateRemoteCartItems = useCallback(async (accountId: string) => {
    const localItems = readCartItemsForAccount(accountId);
    const localOnlyItems = localItems.filter((item) => getRemoteCartItemIds(item.remoteCartItemIds).length === 0);

    const cartResponse = await fetchAwsApi(buildShoppingCartQuery(accountId), {
      method: 'GET',
    });
    const cartPayload = await parseApiResponse(cartResponse);

    if (!isApiResponseOk(cartResponse, cartPayload)) {
      throw new Error(getApiErrorMessage(cartPayload, 'Unable to load your shopping cart.'));
    }

    const remoteCartRows = extractRemoteCartRows(cartPayload);
    const freshLocalItems = readCartItemsForAccount(accountId);

    const remoteItemIds = Array.from(
      new Set(
        remoteCartRows
          .map((row) => (typeof row.item_id === 'string' ? row.item_id.trim() : ''))
          .filter(Boolean)
      )
    );
    // Local-only items are priced from the live catalog as well, so a reload
    // never keeps a price that was saved when the item was added to the cart.
    const catalogProducts = await loadRemoteCatalogProductsByIds(
      [...remoteItemIds, ...localOnlyItems.map((item) => item.itemId)],
      {
        errorMessage: 'Unable to load your shopping cart items.',
        forceRefresh: true,
      }
    );
    const productLookup = catalogProducts
      .reduce<Record<string, ShopProduct>>((accumulator, product) => {
        accumulator[product.id] = product;
        return accumulator;
      }, {});
    const findCatalogProduct = (itemId: string) => productLookup[itemId]
      ?? catalogProducts.find((catalogProduct) =>
        catalogProduct.id === itemId
        || catalogProduct.id.startsWith(itemId)
        || itemId.startsWith(catalogProduct.id)
      );
    const refreshedLocalOnlyItems = localOnlyItems.map((item) => {
      if (!item.itemId) {
        return item;
      }

      return {
        ...item,
        price: resolveCartItemUnitPrice(item, findCatalogProduct(item.itemId)),
      };
    });

    const usedLocalItemIds = new Set<string>();
    const remoteItems = remoteCartRows
      .map((row) => {
        const remoteCartItemId = typeof row.id === 'string' && isUuid(row.id) ? row.id : '';
        const remoteItemId = typeof row.item_id === 'string' && isUuid(row.item_id) ? row.item_id : '';
        const remoteQuantity = typeof row.quantity === 'number' ? row.quantity : Number(row.quantity);

        if (!remoteCartItemId || !remoteItemId || !Number.isInteger(remoteQuantity) || remoteQuantity < 1) {
          return null;
        }

        const normalizedRemoteSize = resolveRemoteCartSizeValue(row);
        const normalizedRemoteFit = normalizeFitLabel(row.fit);
        const normalizedRemoteColor = normalizeColorHex(row.color);
        const normalizedRemoteFabricBlend =
          normalizeFabricBlendLabel(row.fabric_blend)
          ?? getFabricLabelFromMaterial(normalizeMaterialRecord(row.material));
        const normalizedRemoteGender = normalizeGenderLabel(row.gender);

        const localMatchByRemoteId = freshLocalItems.find((item) =>
          !usedLocalItemIds.has(item.id) && item.remoteCartItemIds?.includes(remoteCartItemId)
        );
        const localMatchByItemAndSpec = freshLocalItems.find((item) =>
          !usedLocalItemIds.has(item.id)
          && item.itemId === remoteItemId
          && isSameCartSpec(item, {
            size: normalizedRemoteSize,
            fit: normalizedRemoteFit,
            color: normalizedRemoteColor,
            fabricBlend: normalizedRemoteFabricBlend,
            gender: normalizedRemoteGender,
          })
        );
        const localMatchByItemId = freshLocalItems.find((item) =>
          !usedLocalItemIds.has(item.id) && item.itemId === remoteItemId
        );
        const localMatch = localMatchByRemoteId ?? localMatchByItemAndSpec ?? localMatchByItemId;
        const product = findCatalogProduct(remoteItemId);
        if (!product) {
          return null;
        }

        const hydratedItem = sanitizeStoredCartItem({
          id: localMatch?.id ?? remoteCartItemId,
          itemId: remoteItemId,
          cadFileId: localMatch?.cadFileId,
          s3Key: extractPrimaryS3KeyFromRow(row) || localMatch?.s3Key,
          remoteCartItemIds: [remoteCartItemId],
          measurements: row.measurements ?? localMatch?.measurements,
          size: normalizedRemoteSize ?? localMatch?.size,
          fabricBlend:
            normalizedRemoteFabricBlend
            ?? localMatch?.fabricBlend,
          fit: normalizedRemoteFit ?? localMatch?.fit,
          color: normalizedRemoteColor ?? localMatch?.color,
          material: row.material ?? localMatch?.material,
          gender: normalizedRemoteGender ?? localMatch?.gender,
          type: getCartItemType(product, localMatch?.type),
          title: localMatch?.title ?? resolveLocalizedText(product?.name ?? '', 'en') ?? '',
          description: localMatch?.description
            ?? buildCartDescription({
              categoryLabel: product?.category,
              size: normalizedRemoteSize,
              fit: normalizedRemoteFit,
              fabricLabel: normalizedRemoteFabricBlend,
              gender: normalizedRemoteGender,
            })
            ?? resolveLocalizedText(product?.description ?? '', 'en')
            ?? (product ? `${product.category} item` : ''),
          image: resolveRemoteCartPrimaryImageUrl(row)
            || (typeof row.s3_key === 'string' && row.s3_key.trim()
              ? buildItemPhotoEndpoint(row.s3_key)
              : '')
            || localMatch?.image
            || product?.image
            || '',
          price: resolveCartItemUnitPrice(
            {
              price: localMatch?.price ?? 0,
              itemId: remoteItemId,
            },
            product,
            [row],
          ),
          quantity: remoteQuantity,
          details: localMatch?.details ?? buildCartDetails({
            categoryLabel: product?.category,
            size: normalizedRemoteSize,
            fit: normalizedRemoteFit,
            fabricLabel: normalizedRemoteFabricBlend,
            color: normalizedRemoteColor,
            gender: normalizedRemoteGender,
          }),
        });

        if (hydratedItem && localMatch) {
          usedLocalItemIds.add(localMatch.id);
        }
        return hydratedItem;
      })
      .filter((item): item is CartItem => item !== null);

    return persistItems(accountId, mergeHydratedCartItems(refreshedLocalOnlyItems, remoteItems, usedLocalItemIds));
  }, [persistItems]);

  const hydrateRemoteCartItemsOnce = useCallback(async (accountId: string, force = false) => {
    if (!accountId) {
      return [];
    }

    const now = Date.now();
    const lastHydratedAt = cartHydrationTimestampByAccount.get(accountId) ?? 0;
    if (!force && now - lastHydratedAt < REMOTE_CART_REFRESH_INTERVAL_MS) {
      return readCartItemsForAccount(accountId);
    }

    const existingHydration = cartHydrationPromiseByAccount.get(accountId);
    if (existingHydration) {
      return existingHydration;
    }

    const nextHydration = hydrateRemoteCartItems(accountId)
      .then((hydratedItems) => {
        cartHydrationTimestampByAccount.set(accountId, Date.now());
        return hydratedItems;
      })
      .finally(() => {
        cartHydrationPromiseByAccount.delete(accountId);
      });

    cartHydrationPromiseByAccount.set(accountId, nextHydration);
    return nextHydration;
  }, [hydrateRemoteCartItems]);

  const refreshCart = useCallback(async () => {
    const accountId = getActiveCartAccountId();
    setActiveAccountId(accountId);

    if (!accountId) {
      setItems([]);
      return [];
    }

    // Explicit cart-page refreshes must load the active cart even for an
    // administrator account. The admin exclusion only applies to automatic
    // app-wide hydration, where loading a shopper cart is undesirable.
    const hydratedItems = await hydrateRemoteCartItemsOnce(accountId, true);

    // Do not let a response for a session that changed in flight replace the
    // newly active session's cart.
    if (getActiveCartAccountId() === accountId) {
      setItems(hydratedItems);
    }

    return hydratedItems;
  }, [hydrateRemoteCartItemsOnce]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    let isActive = true;

    const syncCartFromAuth = async () => {
      const nextAccountId = getActiveCartAccountId();
      const previousAccountId = lastSyncedAccountIdRef.current;
      const accountChanged = previousAccountId !== nextAccountId;
      lastSyncedAccountIdRef.current = nextAccountId;
      setActiveAccountId(nextAccountId);

      if (!nextAccountId) {
        if (isActive) {
          setItems([]);
        }
        return;
      }

      const remoteCartEnabled = shouldHydrateRemoteCartItems(nextAccountId);
      if (accountChanged && isActive) {
        setItems(readCartItemsForAccount(nextAccountId));
      }

      if (!remoteCartEnabled) {
        const localItems = readCartItemsForAccount(nextAccountId);
        persistItems(nextAccountId, localItems);
        if (isActive) {
          setItems(localItems);
        }
        return;
      }

      try {
        const hydratedItems = await hydrateRemoteCartItemsOnce(nextAccountId, accountChanged);

        if (isActive) {
          setItems(hydratedItems);
        }
      } catch {
        if (isActive) {
          setItems(readCartItemsForAccount(nextAccountId));
        }
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (
        event.key === null ||
        event.key === AUTH_FLAG_KEY ||
        event.key === AUTH_TOKEN_KEY ||
        event.key === AUTH_USER_KEY ||
        event.key === getCartStorageKey(getActiveCartAccountId())
      ) {
        syncCartFromAuth();
      }
    };

    void syncCartFromAuth();
    window.addEventListener(AUTH_STATE_CHANGE_EVENT, syncCartFromAuth);
    window.addEventListener('storage', handleStorage);

    return () => {
      isActive = false;
      window.removeEventListener(AUTH_STATE_CHANGE_EVENT, syncCartFromAuth);
      window.removeEventListener('storage', handleStorage);
    };
  }, [hydrateRemoteCartItemsOnce, persistItems]);

  const addItem = useCallback(async (item: Omit<CartItem, 'id'>, options?: { syncRemote?: boolean }) => {
    if (!activeAccountId) {
      return;
    }

    const remoteCartEnabled = options?.syncRemote === false ? false : shouldSyncRemoteCart(activeAccountId);
    const nextId =
      typeof globalThis.crypto?.randomUUID === 'function'
        ? globalThis.crypto.randomUUID()
        : `cart-${Date.now()}`;
    const sanitizedItem = sanitizeStoredCartItem({
      ...item,
      id: nextId,
    });

    if (!sanitizedItem) {
      throw new Error('Unable to save an invalid cart item.');
    }

    const isRemoteBackedItem = getRemoteCartItemIds(sanitizedItem.remoteCartItemIds).length > 0;
    const existingItem = isRemoteBackedItem
      ? undefined
      : items.find((entry) => {
          return (
            (remoteCartEnabled || getRemoteCartItemIds(entry.remoteCartItemIds).length === 0)
            && getCartItemConfigurationSignature(entry) === getCartItemConfigurationSignature(sanitizedItem)
          );
        });

    if (existingItem) {
      const mergedItem = sanitizeStoredCartItem({
        ...existingItem,
        quantity: Math.min(existingItem.quantity + sanitizedItem.quantity, 99),
      });

      if (!mergedItem) {
        throw new Error('Unable to update this cart item.');
      }

      let syncedItem = remoteCartEnabled
        ? mergedItem
        : {
            ...mergedItem,
            remoteCartItemIds: undefined,
          };

      if (remoteCartEnabled) {
        const existingRemoteCartItemIds = getRemoteCartItemIds(existingItem.remoteCartItemIds);
        if (existingRemoteCartItemIds.length > 0) {
          await updateRemoteCartItemQuantity(existingRemoteCartItemIds[0], mergedItem.quantity);
          syncedItem = {
            ...mergedItem,
            remoteCartItemIds: [existingRemoteCartItemIds[0]],
          };
        } else {
          const remoteCartItemId = await createRemoteCartItem(mergedItem);
          syncedItem = {
            ...mergedItem,
            remoteCartItemIds: remoteCartItemId ? [remoteCartItemId] : undefined,
          };
        }
      }

      setItems((prevItems) => {
        const nextItems = prevItems.map((entry) => (entry.id === existingItem.id ? syncedItem : entry));
        return persistItems(activeAccountId, nextItems);
      });
      return;
    }

    let syncedItem = sanitizedItem;

    if (remoteCartEnabled && getRemoteCartItemIds(sanitizedItem.remoteCartItemIds).length === 0) {
      const remoteCartItemId = await createRemoteCartItem(sanitizedItem);
      syncedItem = {
        ...sanitizedItem,
        remoteCartItemIds: remoteCartItemId ? [remoteCartItemId] : undefined,
      };
    }

    setItems((prevItems) => {
      const nextItems = [...prevItems, syncedItem];
      return persistItems(activeAccountId, nextItems);
    });
  }, [activeAccountId, createRemoteCartItem, items, persistItems, updateRemoteCartItemQuantity]);

  const removeItem = useCallback(async (id: string) => {
    if (!activeAccountId) {
      return;
    }

    const remoteCartEnabled = shouldSyncRemoteCart(activeAccountId);
    const existingItem = items.find((item) => item.id === id);
    if (!existingItem) {
      return;
    }

    if (remoteCartEnabled) {
      try {
        await deleteRemoteCartItems(existingItem.remoteCartItemIds);
      } catch {
        return;
      }
    }

    setItems((prevItems) => {
      const nextItems = prevItems.filter((item) => item.id !== id);
      return persistItems(activeAccountId, nextItems);
    });
  }, [activeAccountId, deleteRemoteCartItems, items, persistItems]);

  const updateQuantity = useCallback(async (id: string, quantity: number) => {
    if (!activeAccountId) {
      return;
    }

    const remoteCartEnabled = shouldSyncRemoteCart(activeAccountId);
    const existingItem = items.find((item) => item.id === id);
    if (!existingItem) {
      return;
    }

    const safeQuantity = Math.min(Math.max(Math.trunc(quantity), 0), 99);

    if (safeQuantity <= 0) {
      await removeItem(id);
      return;
    }

    const nextItem = sanitizeStoredCartItem({
      ...existingItem,
      quantity: safeQuantity,
    });

    if (!nextItem) {
      return;
    }

    const existingRemoteCartItemIds = getRemoteCartItemIds(existingItem.remoteCartItemIds);
    if (remoteCartEnabled && existingRemoteCartItemIds.length > 0) {
      const [primaryRemoteCartItemId, ...staleRemoteCartItemIds] = existingRemoteCartItemIds;
      try {
        await updateRemoteCartItemQuantity(primaryRemoteCartItemId, safeQuantity);

        if (staleRemoteCartItemIds.length > 0) {
          await deleteRemoteCartItems(staleRemoteCartItemIds);
        }
      } catch {
        return;
      }
    }

    const syncedItem = remoteCartEnabled
      ? {
          ...nextItem,
          remoteCartItemIds: existingRemoteCartItemIds.length > 0
            ? [existingRemoteCartItemIds[0]]
            : existingItem.remoteCartItemIds,
        }
      : {
          ...nextItem,
          remoteCartItemIds: undefined,
        };

    setItems((prevItems) => {
      const nextItems = prevItems.map((item) => (item.id === id ? syncedItem : item));
      return persistItems(activeAccountId, nextItems);
    });
  }, [activeAccountId, deleteRemoteCartItems, items, persistItems, removeItem, updateRemoteCartItemQuantity]);

  const updateItem = useCallback(async (id: string, updates: Partial<Omit<CartItem, 'id'>>) => {
    if (!activeAccountId) {
      return;
    }

    const remoteCartEnabled = shouldSyncRemoteCart(activeAccountId);
    const existingItem = items.find((item) => item.id === id);
    if (!existingItem) {
      return;
    }

    const sanitizedItem = sanitizeStoredCartItem({
      ...existingItem,
      ...updates,
      id,
    });

    if (!sanitizedItem) {
      return;
    }

    const isRemoteBackedItem =
      getRemoteCartItemIds(sanitizedItem.remoteCartItemIds).length > 0
      || getRemoteCartItemIds(existingItem.remoteCartItemIds).length > 0;
    const duplicateItem = isRemoteBackedItem
      ? undefined
      : items.find((item) => {
          return (
            item.id !== id
            && getRemoteCartItemIds(item.remoteCartItemIds).length === 0
            && getCartItemConfigurationSignature(item) === getCartItemConfigurationSignature(sanitizedItem)
          );
        });

    if (duplicateItem) {
      const mergedItem = sanitizeStoredCartItem({
        ...duplicateItem,
        ...sanitizedItem,
        id: duplicateItem.id,
        quantity: Math.min(duplicateItem.quantity + sanitizedItem.quantity, 99),
      });

      if (!mergedItem) {
        return;
      }

      const syncedMergedItem = remoteCartEnabled
        ? mergedItem
        : {
            ...mergedItem,
            remoteCartItemIds: undefined,
          };

      setItems((prevItems) => {
        const nextItems = prevItems
          .filter((item) => item.id !== id)
          .map((item) => (item.id === duplicateItem.id ? syncedMergedItem : item));
        return persistItems(activeAccountId, nextItems);
      });
      return;
    }

    const syncedItem = remoteCartEnabled
      ? {
          ...sanitizedItem,
          remoteCartItemIds:
            getRemoteCartItemIds(sanitizedItem.remoteCartItemIds).length > 0
              ? getRemoteCartItemIds(sanitizedItem.remoteCartItemIds)
              : existingItem.remoteCartItemIds,
        }
      : {
          ...sanitizedItem,
          remoteCartItemIds: undefined,
        };

    setItems((prevItems) => {
      const nextItems = prevItems.map((item) => (item.id === id ? syncedItem : item));
      return persistItems(activeAccountId, nextItems);
    });
  }, [activeAccountId, items, persistItems]);

  const clearCart = useCallback(async (options?: { syncRemote?: boolean }) => {
    if (options?.syncRemote !== false && shouldSyncRemoteCart(activeAccountId)) {
      const remoteCartItemIds = items.flatMap((item) => item.remoteCartItemIds ?? []);
      await deleteRemoteCartItems(remoteCartItemIds);
    }
    setItems([]);
    if (activeAccountId) {
      persistItems(activeAccountId, []);
    }
  }, [activeAccountId, deleteRemoteCartItems, items, persistItems]);

  const getTotalPrice = useCallback(() => {
    return items.reduce((total, item) => total + item.price * item.quantity, 0);
  }, [items]);

  const getItemCount = useCallback(() => {
    return items.reduce((total, item) => total + item.quantity, 0);
  }, [items]);

  const value: CartContextType = {
    items,
    refreshCart,
    addItem,
    removeItem,
    updateQuantity,
    updateItem,
    clearCart,
    getTotalPrice,
    getItemCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  return useContext(CartContext);
}
